/**
 * THE CARDS.
 *
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE: a card shows the WORDS, not the type. A "say"
 * node reads as the sentence the caller will hear, in quotation marks, at a size you can read
 * across a desk. A canvas of boxes labelled "say · say · branch · say" is a graph toy; a
 * canvas you can read aloud and recognise as your own phone line is the product.
 *
 * Three cards are deliberately not like the others, and each difference is a doctrine:
 *
 *   do    is orchid and says "proposed for approval" on its face, because it is the only
 *         node that changes the world and doctrine 2 says nothing auto-runs.
 *   page  carries a padlock and the words "never connected to a technician", because
 *         doctrine 1 is the thing this product is sold on and a buyer looking at the canvas
 *         should see it without being told.
 *   arm   is the heaviest object on the canvas — a solid header band, not another white
 *         tile — because it is the only node whose effect outlives the call. Every later
 *         caller hears the change. It should not look like another sentence.
 *
 * Errors land ON the card. A compiler message at the bottom of the screen makes you hunt;
 * a red hairline and the sentence under the offending node does not.
 */
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import type { ReactNode } from 'react';

import { Icon } from '../Icon.tsx';
import { useFlowUi } from './FlowUiContext.tsx';
import { EMPTY_LINE, MESSAGE_CATEGORIES, NODE_SPECS, toolLabel } from './model.ts';
import type { NdData, NdKind } from './model.ts';
import type { NdNode } from './graph.ts';

// ------------------------------------------------------------------------------- the shell

type ShellProps = {
  id: string;
  kind: NdKind;
  selected: boolean;
  /** The words. This is the point of the card. */
  children: ReactNode;
  /** Case outlets, for branch. */
  outlets?: ReactNode;
  /** A doctrine line, rendered as the locked idiom. */
  rule?: string;
  aside?: ReactNode;
};

function Shell({ id, kind, selected, children, outlets, rule, aside }: ShellProps): ReactNode {
  const spec = NODE_SPECS[kind];
  const ui = useFlowUi();
  const errors = ui.issues.get(id) ?? [];
  const warns = ui.warnings.get(id) ?? [];

  const classes = [
    'ndf-node',
    `ndf-node--${spec.tone}`,
    selected ? 'is-selected' : '',
    errors.length ? 'is-error' : '',
    warns.length && !errors.length ? 'is-warn' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} data-kind={kind} data-testid={`flow-node-${id}`}>
      {spec.hasIn ? <Handle type="target" position={Position.Top} className="ndf-handle ndf-handle--in" /> : null}

      <div className="ndf-node-head">
        <span className="ndf-node-chip" aria-hidden="true">
          <Icon name={spec.icon} size={13} />
        </span>
        <span className="ndf-node-eyebrow">{spec.eyebrow}</span>
        {aside ? <span className="ndf-node-aside">{aside}</span> : null}
        {ui.readOnly ? null : (
          <button
            type="button"
            className="ndf-node-edit"
            onClick={(event) => {
              event.stopPropagation();
              ui.onEdit(id);
            }}
            title="Edit this step"
          >
            <Icon name="edit" size={12} />
            <span className="nd-sr">Edit this step</span>
          </button>
        )}
      </div>

      <div className="ndf-node-body">{children}</div>

      {outlets}

      {rule ? (
        <div className="ndf-node-rule">
          <Icon name="lock" size={11} />
          <span>{rule}</span>
        </div>
      ) : null}

      {errors.map((message) => (
        <div key={message} className="ndf-node-issue" role="status">
          <Icon name="alert-triangle" size={11} />
          <span>{message}</span>
        </div>
      ))}
      {errors.length === 0
        ? warns.map((message) => (
            <div key={message} className="ndf-node-issue ndf-node-issue--warn" role="status">
              <Icon name="info" size={11} />
              <span>{message}</span>
            </div>
          ))
        : null}

      {spec.hasOut && !spec.multiOut ? (
        <Handle type="source" position={Position.Bottom} className="ndf-handle ndf-handle--out" />
      ) : null}
    </div>
  );
}

/** The words, or a real sentence explaining that there are none yet. Never a blank card. */
function Words({ kind, text, quoted }: { kind: NdKind; text: string; quoted?: boolean }): ReactNode {
  const value = text.trim();
  if (!value) return <p className="ndf-words ndf-words--empty">{EMPTY_LINE[kind]}</p>;
  return <p className={quoted ? 'ndf-words ndf-words--quote' : 'ndf-words'}>{quoted ? `“${value}”` : value}</p>;
}

// -------------------------------------------------------------------------------- the ten

const EntryNode = memo(function EntryNode({ id, selected }: NodeProps<NdNode>): ReactNode {
  const ui = useFlowUi();
  return (
    <Shell id={id} kind="entry" selected={Boolean(selected)}>
      <p className="ndf-words">A call comes in.</p>
      {ui.trigger ? <p className="ndf-sub">{ui.trigger}</p> : null}
    </Shell>
  );
});

const SayNode = memo(function SayNode({ id, data, selected }: NodeProps<NdNode>): ReactNode {
  return (
    <Shell id={id} kind="say" selected={Boolean(selected)}>
      <Words kind="say" text={str(data, 'text')} quoted />
    </Shell>
  );
});

const AskNode = memo(function AskNode({ id, data, selected }: NodeProps<NdNode>): ReactNode {
  const key = str(data, 'key');
  return (
    <Shell
      id={id}
      kind="ask"
      selected={Boolean(selected)}
      aside={key ? <span className="ndf-tag ndf-tag--mono">{key}</span> : null}
    >
      <Words kind="ask" text={str(data, 'text')} quoted />
      <p className="ndf-sub">
        {key ? <>Remembered as <b className="nd-mono">{key}</b>.</> : 'The answer has no name yet, so nothing can branch on it.'}
      </p>
    </Shell>
  );
});

const BranchNode = memo(function BranchNode({ id, data, selected }: NodeProps<NdNode>): ReactNode {
  const on = str(data, 'on');
  const cases = Array.isArray(data.cases) ? (data.cases as unknown[]).filter((c): c is string => typeof c === 'string') : [];
  return (
    <Shell
      id={id}
      kind="branch"
      selected={Boolean(selected)}
      outlets={
        <ul className="ndf-cases">
          {cases.length === 0 ? (
            <li className="ndf-case ndf-case--empty">No outcomes yet.</li>
          ) : (
            cases.map((value) => (
              <li key={value} className="ndf-case">
                <span className="ndf-case-when">if</span>
                <span className="ndf-case-value nd-mono">{value}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={value}
                  className="ndf-handle ndf-handle--case"
                />
              </li>
            ))
          )}
        </ul>
      }
    >
      <p className="ndf-words">
        {on ? <>Depending on <b className="nd-mono">{on}</b></> : EMPTY_LINE.branch}
      </p>
    </Shell>
  );
});

const LookupNode = memo(function LookupNode({ id, data, selected }: NodeProps<NdNode>): ReactNode {
  const tool = str(data, 'tool');
  return (
    /*
     * NO "Read-only" TAG. The rule line at the foot of this card already says "Read-only.
     * Nothing outside the call changes." in full, and the tag was the same word twice —
     * bought at the price of the card's own name, which measured 117px against the 155px
     * "THE ASSISTANT LOOKS UP" needs and read "THE ASSISTANT L…" on every look-up card in
     * every starter. The doctrine keeps its sentence; the card gets its name back.
     */
    <Shell
      id={id}
      kind="lookup"
      selected={Boolean(selected)}
      rule={NODE_SPECS.lookup.rule ?? ''}
    >
      <Words kind="lookup" text={tool ? toolLabel(tool) : ''} />
    </Shell>
  );
});

const DoNode = memo(function DoNode({ id, data, selected }: NodeProps<NdNode>): ReactNode {
  const tool = str(data, 'tool');
  return (
    /*
     * Same as look-up: "Needs approval" and the rule line "Proposed, not performed. It waits
     * for a person to approve it." are the same promise, and the tag was the weaker of the
     * two while costing this card its eyebrow. Doctrine 2 is still on the card's face — in
     * the sentence, in the orchid, and in the eyebrow "THE ASSISTANT PROPOSES", which is now
     * legible instead of clipped.
     */
    <Shell
      id={id}
      kind="do"
      selected={Boolean(selected)}
      rule={NODE_SPECS.do.rule ?? ''}
    >
      <Words kind="do" text={tool ? toolLabel(tool) : ''} />
    </Shell>
  );
});

const PageNode = memo(function PageNode({ id, data, selected }: NodeProps<NdNode>): ReactNode {
  const who = str(data, 'who');
  return (
    <Shell id={id} kind="page" selected={Boolean(selected)} rule={NODE_SPECS.page.rule ?? ''}>
      <p className="ndf-words">The caller leaves a voicemail, then on-call is paged.</p>
      {who ? <p className="ndf-sub">Note on the page: {who}</p> : null}
    </Shell>
  );
});

const MessageNode = memo(function MessageNode({ id, data, selected }: NodeProps<NdNode>): ReactNode {
  const category = str(data, 'category') || 'customer';
  const label = MESSAGE_CATEGORIES.find((c) => c.value === category)?.label ?? category;
  return (
    <Shell id={id} kind="message" selected={Boolean(selected)}>
      <p className="ndf-words">The caller leaves a message.</p>
      <p className="ndf-sub">Filed as {label.toLowerCase()}.</p>
    </Shell>
  );
});

const ArmNode = memo(function ArmNode({ id, data, selected }: NodeProps<NdNode>): ReactNode {
  const ui = useFlowUi();
  const key = str(data, 'situation');
  return (
    <Shell
      id={id}
      kind="arm"
      selected={Boolean(selected)}
      rule={NODE_SPECS.arm.rule ?? ''}
    >
      <p className="ndf-words ndf-words--mode">{key ? ui.situationLabel(key) : EMPTY_LINE.arm}</p>
      {key ? <p className="ndf-sub">Held until somebody releases it.</p> : null}
    </Shell>
  );
});

const EndNode = memo(function EndNode({ id, selected }: NodeProps<NdNode>): ReactNode {
  return (
    <Shell id={id} kind="end" selected={Boolean(selected)}>
      <p className="ndf-words">The call ends.</p>
    </Shell>
  );
});

function str(data: NdData, key: string): string {
  const value = data[key];
  return typeof value === 'string' ? value : '';
}

/**
 * Passed to <ReactFlow nodeTypes={…} />. It must be a stable object — a fresh one on every
 * render remounts every node, which loses focus mid-typing and makes the canvas feel broken
 * in exactly the way a canvas must not.
 */
export const NODE_COMPONENTS = {
  entry: EntryNode,
  say: SayNode,
  ask: AskNode,
  branch: BranchNode,
  lookup: LookupNode,
  do: DoNode,
  page: PageNode,
  message: MessageNode,
  arm: ArmNode,
  end: EndNode,
} as const;
